import { useState, type FormEvent } from "react";
import { useOnboardingForm } from "../useOnboardingForm";
import { CheckableCard, StepHeading, StepShell } from "@codel/dova-ds";
import { TextField } from "../../components/TextField";

import { Button } from "../components/Button";
import type { StepProps } from "../flowConfig";
import type { FocusArea } from "../../types";
// Options live in `answerVocabulary.ts`. `intimacy` is why: it once read
// "Bringing back intimacy and closeness" and now reads "Improving sex life and
// desire", so answers stored under it predate the question they now appear to
// answer. The lock beside the vocabulary stops that happening again silently.
import { FOCUS_AREA_OPTIONS as FOCUS_OPTIONS , FREE_TEXT_QUESTION } from "../answerVocabulary";

const MAX_FOCUS = 3;

export function S5aFocus({ onNext, onBack, currentStepIndex, totalSteps }: StepProps) {
  const { state, updateAnswers } = useOnboardingForm();
  const [focus, setFocus] = useState<FocusArea[]>(state.answers.focusAreas);
  const [otherText, setOtherText] = useState(state.answers.focusAreasOther ?? "");
  const [error, setError] = useState("");

  const atMax = focus.length >= MAX_FOCUS;

  const toggleFocus = (value: string, checked: boolean) => {
    const v = value as FocusArea;
    setFocus((prev) => {
      if (checked) {
        if (prev.includes(v) || prev.length >= MAX_FOCUS) return prev;
        return [...prev, v];
      }
      return prev.filter((x) => x !== v);
    });
    if (error) setError("");
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (focus.length === 0) {
      setError("Pick at least one focus area.");
      return;
    }
    setError("");
    updateAnswers({
      focusAreas: focus,
      focusAreasOther: focus.includes("something_else")
        ? otherText.trim() || null
        : null,
    });
    onNext();
  };

  return (
    <StepShell
      showBack
      onBack={onBack}
      currentStep={currentStepIndex}
      totalSteps={totalSteps}
      onSubmit={handleSubmit}
      footer={<Button type="submit">Continue</Button>}
      // PINNED, because this is the longest list in the flow: eight options over a
      // 718px scroller that the heading was taking 103px of. Scrolled, the question
      // left the screen exactly as you reached "Something else", which is the moment
      // you most need to remember it was "up to 3".
      //
      // "Choose up to 3" rides in the heading's supporting slot rather than in the
      // title: it is a CONSTRAINT on the answer, not part of the question, and a
      // heading that carries its own small print stops reading as a question. Pinned,
      // it also stays visible while you spend your three.
      heading={
        <StepHeading title="What would you like to work on together?">
          Choose up to 3.
        </StepHeading>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-4">
          {FOCUS_OPTIONS.map((opt) => {
            const checked = focus.includes(opt.value);
            return (
              <CheckableCard
                key={opt.value}
                label={opt.label}
                checked={checked}
                disabled={!checked && atMax}
                // CheckableCard reports the boolean only; the option's value is
                // closed over here rather than re-injected by a wrapper.
                onChange={(next) => toggleFocus(opt.value, next)}
              />
            );
          })}
        </div>
        {focus.includes("something_else") && (
          <TextField
            id="focus-other"
            aria-label={FREE_TEXT_QUESTION.focus_areas_other}
            value={otherText}
            onChange={setOtherText}
            placeholder="What else would you like to focus on?"
            autoFocus
          />
        )}
        {error && (
          <p className="text-xs leading-[1.5] text-[var(--danger)]">{error}</p>
        )}
      </div>
    </StepShell>
  );
}
